/**
 * useNavigation Hook
 * 
 * Custom hook for managing tab navigation state and URL synchronization
 */

import { useState, useEffect, useCallback } from 'react';
import { TabName, getCurrentTab, navigateToTab, getTabConfig } from '../utils/navigation';

interface UseNavigationResult {
  activeTab: TabName;
  setActiveTab: (tab: TabName) => void;
  navigateTo: (tab: TabName) => void;
  canGoBack: boolean;
  goBack: () => void;
}

/**
 * Hook for managing plugin manager navigation
 */
export function useNavigation(initialTab?: TabName): UseNavigationResult {
  const [activeTab, setActiveTab] = useState<TabName>(initialTab || getCurrentTab());

  // Sync with URL changes (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      const tab = getCurrentTab();
      setActiveTab(tab);
    };

    // Listen for custom navigation events
    const handleTabNavigate = (event: CustomEvent) => {
      const { tab } = event.detail;
      if (tab) {
        setActiveTab(tab);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('tab-navigate', handleTabNavigate as EventListener);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('tab-navigate', handleTabNavigate as EventListener);
      };
    }
  }, []);

  // Navigate to tab with URL update
  const navigateTo = useCallback((tab: TabName) => {
    setActiveTab(tab);
    navigateToTab(tab);
  }, []);

  // Check if can go back
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;

  // Go back
  const goBack = useCallback(() => {
    if (typeof window !== 'undefined' && window.history) {
      window.history.back();
    }
  }, []);

  return {
    activeTab,
    setActiveTab,
    navigateTo,
    canGoBack,
    goBack
  };
}

/**
 * Hook for keyboard shortcuts
 */
export function useKeyboardShortcuts(onTabChange: (tab: TabName) => void): void {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Alt+1-6 for quick tab switching
      if (e.altKey && e.key >= '1' && e.key <= '6') {
        e.preventDefault();
        const tabs: TabName[] = ['overview', 'installed', 'marketplace', 'create', 'drafts', 'security'];
        const tabIndex = parseInt(e.key) - 1;
        if (tabIndex < tabs.length) {
          onTabChange(tabs[tabIndex]);
        }
      }

      // Alt+Left/Right for tab navigation
      if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        const tabs: TabName[] = ['overview', 'installed', 'marketplace', 'create', 'drafts', 'security'];
        const currentTab = getCurrentTab();
        const currentIndex = tabs.indexOf(currentTab);
        
        if (e.key === 'ArrowLeft' && currentIndex > 0) {
          onTabChange(tabs[currentIndex - 1]);
        } else if (e.key === 'ArrowRight' && currentIndex < tabs.length - 1) {
          onTabChange(tabs[currentIndex + 1]);
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [onTabChange]);
}

